import { useMemo } from 'react';
import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import type { LookalikePersona, LookalikeView } from '@repo/contracts';

import type {
  LookalikeAxisField,
  LookalikeFilterField,
  LookalikeViewMode,
} from '../store/persona-lookalikes-store';
import {
  companySizeSlot,
  slotFillClass,
  topCompanySizes,
} from './lookalike-colors';
import {
  AXIS_LABELS,
  CHART_HEIGHT,
  CHART_PADDING,
  CHART_WIDTH,
  fieldAxisPosition,
} from './lookalike-layout';
import { similarityPositions } from './lookalike-similarity';

/** Similarity view has no real field on either axis — both are components of
 *  one PCA projection over every field at once — so there's no entry in
 *  `AXIS_LABELS` to borrow. Named generically rather than left blank: a
 *  chart with unlabelled axes reads as broken, not as "there's nothing to
 *  say here." */
const SIMILARITY_AXIS_LABELS = {
  x: 'Similarity — component 1',
  y: 'Similarity — component 2',
};

interface PlottedPoint {
  colorClass: string;
  lookalike: LookalikePersona;
  opacity: number;
  x: number;
  y: number;
}

function matchesFilter(
  lookalike: LookalikePersona,
  field: LookalikeFilterField | null,
  value: string | null,
): boolean {
  if (!field || !value) return true;
  return lookalike[field] === value;
}

function usePoints(
  view: LookalikeView,
  viewMode: LookalikeViewMode,
  xField: LookalikeAxisField,
  yField: LookalikeAxisField,
  filterField: LookalikeFilterField | null,
  filterValue: string | null,
): PlottedPoint[] {
  const top = useMemo(() => topCompanySizes(view), [view]);

  const positions = useMemo(() => {
    if (viewMode === 'similarity') {
      return similarityPositions(
        view,
        { max: CHART_WIDTH - CHART_PADDING.right, min: CHART_PADDING.left },
        { max: CHART_HEIGHT - CHART_PADDING.bottom, min: CHART_PADDING.top },
      );
    }
    return view.lookalikes.map((lookalike) => ({
      x: fieldAxisPosition(
        lookalike,
        xField,
        view.spec.schema,
        CHART_PADDING.left,
        CHART_WIDTH - CHART_PADDING.right,
        1,
      ),
      y: fieldAxisPosition(
        lookalike,
        yField,
        view.spec.schema,
        CHART_HEIGHT - CHART_PADDING.bottom,
        CHART_PADDING.top,
        2,
      ),
    }));
  }, [view, viewMode, xField, yField]);

  return view.lookalikes.map((lookalike, i) => {
    const slot =
      viewMode === 'similarity'
        ? lookalike.cluster
        : companySizeSlot(lookalike, top);
    return {
      colorClass: slotFillClass(slot),
      lookalike,
      opacity: matchesFilter(lookalike, filterField, filterValue) ? 0.82 : 0.12,
      x: positions[i]!.x,
      y: positions[i]!.y,
    };
  });
}

function LookalikeTooltip({
  active,
  payload,
}: {
  active?: boolean;
  payload?: { payload?: PlottedPoint }[];
}) {
  const point = active ? payload?.[0]?.payload : undefined;
  if (!point) return null;
  const { lookalike } = point;
  return (
    <div className="rounded-card border border-hairline bg-surface px-3 py-2.5 shadow-soft">
      <p className="text-ui-strong font-semibold text-text-primary">
        {lookalike.jobTitle}
      </p>
      <dl className="mt-1.5 space-y-0.5 text-caption text-text-secondary">
        <div className="flex justify-between gap-4">
          <dt className="text-text-muted">Company size</dt>
          <dd>{lookalike.companySize}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-text-muted">Location</dt>
          <dd>{lookalike.location}</dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-text-muted">Experience</dt>
          <dd className="font-mono tabular-nums">
            {lookalike.experienceYears} yrs
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-text-muted">Goal</dt>
          <dd className="max-w-[16ch] truncate" title={lookalike.goal}>
            {lookalike.goal}
          </dd>
        </div>
        <div className="flex justify-between gap-4">
          <dt className="text-text-muted">Pain point</dt>
          <dd className="max-w-[16ch] truncate" title={lookalike.painPoint}>
            {lookalike.painPoint}
          </dd>
        </div>
      </dl>
    </div>
  );
}

/**
 * The population, plotted two ways — see `LookalikeSchema`'s own doc for the
 * distinction: field view maps real field values straight onto axes, no
 * model involved; similarity view is a real PCA projection over every field
 * at once (`similarityPositions`). Positioning is computed here, not by
 * Recharts' own scales — every axis is fixed to `[0, CHART_WIDTH/HEIGHT]`
 * with ticks hidden. The axis *names* are plain HTML around the chart, not
 * SVG text — `ChartCard`'s own note on HTML over SVG for anything textual
 * applies here too — so they still show up even though the toolbar's
 * dropdowns already say the same thing for field view; similarity view has
 * no dropdown to say it at all.
 */
export function LookalikesChart({
  filterField,
  filterValue,
  view,
  viewMode,
  xField,
  yField,
}: {
  filterField: LookalikeFilterField | null;
  filterValue: string | null;
  view: LookalikeView;
  viewMode: LookalikeViewMode;
  xField: LookalikeAxisField;
  yField: LookalikeAxisField;
}) {
  const points = usePoints(
    view,
    viewMode,
    xField,
    yField,
    filterField,
    filterValue,
  );

  const xLabel =
    viewMode === 'field' ? AXIS_LABELS[xField] : SIMILARITY_AXIS_LABELS.x;
  const yLabel =
    viewMode === 'field' ? AXIS_LABELS[yField] : SIMILARITY_AXIS_LABELS.y;

  return (
    <div className="flex items-stretch gap-1.5">
      <div className="flex w-4 shrink-0 items-center justify-center">
        <span
          className="whitespace-nowrap text-tag text-text-muted"
          style={{ transform: 'rotate(180deg)', writingMode: 'vertical-rl' }}
        >
          {yLabel}
        </span>
      </div>
      <div className="min-w-0 flex-1">
        <div style={{ height: CHART_HEIGHT }}>
          <ResponsiveContainer height="100%" width="100%">
            <ScatterChart margin={{ bottom: 4, left: 4, right: 4, top: 4 }}>
              <CartesianGrid
                className="stroke-viz-grid"
                strokeDasharray="2 4"
              />
              <XAxis
                axisLine={false}
                dataKey="x"
                domain={[0, CHART_WIDTH]}
                tick={false}
                tickLine={false}
                type="number"
              />
              <YAxis
                axisLine={false}
                dataKey="y"
                domain={[0, CHART_HEIGHT]}
                tick={false}
                tickLine={false}
                type="number"
              />
              <Tooltip content={<LookalikeTooltip />} cursor={false} />
              <Scatter
                data={points}
                isAnimationActive={false}
                shape={(props: {
                  cx?: number;
                  cy?: number;
                  payload?: PlottedPoint;
                }) => (
                  <circle
                    className={props.payload?.colorClass}
                    cx={props.cx}
                    cy={props.cy}
                    opacity={props.payload?.opacity}
                    r={4.5}
                  />
                )}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-1 text-center text-tag text-text-muted">{xLabel}</p>
      </div>
    </div>
  );
}
